import React,{useEffect,useState} from "react";
import {api} from "../api";
import {useUser} from "../UserContext";
import AdminPublicHomepage from "./AdminPublicHomepage";
import EditorialScheduleV1 from "./EditorialScheduleV1";
import SocialMediaKitV1 from "./SocialMediaKitV1";

// The delegated workspace reuses the canonical content APIs and the existing
// homepage manager. It does not expose the administrator's user/security tools.
const FIELDS = [
  {key:"writer",title:"Writer Spotlight",fields:[
    ["name","Writer name"],["monthLabel","Month"],["imageUrl","Image URL"],
    ["teaser","Short introduction (English)"],["teaserFr","Short introduction (French)"],
    ["fullText","Full text (English)"],["fullTextFr","Full text (French)"],["works","Selected works (one per line)"]
  ]},
  {key:"book",title:"Book Spotlight",fields:[
    ["title","Book title (English)"],["titleFr","Book title (French)"],["author","Author"],
    ["coverUrl","Cover URL (English)"],["coverUrlFr","Cover URL (French)"],
    ["teaser","Introduction (English)"],["teaserFr","Introduction (French)"],
    ["fullText","Full description (English)"],["fullTextFr","Full description (French)"],["linkUrl","Book link URL"]
  ]},
];
function storedValue(value,key) {
  if (key==="works") return Array.isArray(value)?value.join("\n"):String(value||"");
  return typeof value==="string" ? value : "";
}
export default function ContentStewardWorkspaceV1({lang="en"}) {
  const {isAdmin,isContentSteward}=useUser();
  const authorised=isAdmin||isContentSteward;
  const [area,setArea]=useState("spotlight");
  const [draft,setDraft]=useState(null);
  const [loading,setLoading]=useState(false);
  const [saving,setSaving]=useState(false);
  const [dirty,setDirty]=useState(false);
  const [message,setMessage]=useState("");
  const [error,setError]=useState("");

  useEffect(()=>{
    if(!authorised||area!=="spotlight")return;
    let current=true;
    setLoading(true);setError("");
    api("/api/home/spotlight/editor",{method:"GET"}).then(data=>{
      if(!current)return;
      setDraft(data?.spotlight||null);
      setDirty(false);
    }).catch(e=>{if(current)setError(e?.data?.message||e?.message||"Could not load Home Spotlight.");})
      .finally(()=>{if(current)setLoading(false);});
    return()=>{current=false;};
  },[authorised,area]);
  if(!authorised)return <div className="page"><div className="card">Content Steward appointment required.</div></div>;
  const change=(section,key,value)=>{setDraft(old=>({...old,[section]:{...(old?.[section]||{}),[key]:value}}));setDirty(true);setMessage("");};
  const save=async()=>{
    if(!draft||saving)return;
    setSaving(true);setError("");setMessage("");
    try{
      const data=await api("/api/home/spotlight",{method:"PUT",body:JSON.stringify({
        writer:{...(draft.writer||{}),works:Array.isArray(draft.writer?.works)?draft.writer.works:String(draft.writer?.works||"").split("\n").map(x=>x.trim()).filter(Boolean)},
        book:draft.book||{}
      })});
      if(!data?.ok)throw Error("Save failed.");
      setDraft(data.spotlight);setDirty(false);setMessage("Published successfully.");
    }catch(e){setError(e?.data?.message||e?.message||"Unable to publish changes.");}
    finally{setSaving(false);}
  };
  const tabStyle=selected=>({padding:"10px 16px",borderRadius:20,cursor:"pointer",border:"1px solid #c4d9cd",background:selected?"#116f43":"white",color:selected?"white":"#165a3d",fontWeight:700});
  return <div className="page" style={{maxWidth:1100,margin:"0 auto"}}>
    <div className="ns-card" style={{padding:20,marginBottom:18}}>
      <h1 style={{margin:"0 0 8px"}}>{lang==="fr"?"Responsable du contenu":"Content Steward"}</h1>
      <p className="ns-card-meta">Editorial updates only. Account management, moderation, manuscript governance and security settings remain reserved for administrators.</p>
      <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
        <button type="button" style={tabStyle(area==="spotlight")} onClick={()=>setArea("spotlight")}>Home Spotlight</button>
        <button type="button" style={tabStyle(area==="public")} onClick={()=>setArea("public")}>Weekly Puzzle & Featured Reading</button>
        <button type="button" style={tabStyle(area==="schedule")} onClick={()=>setArea("schedule")}>Editorial Calendar · Automatic Rotation</button>
        <button type="button" style={tabStyle(area==="social")} onClick={()=>setArea("social")}>{lang==="fr"?"Réseaux sociaux":"Social Media"}</button>
      </div>
    </div>
    {area==="public" ? <AdminPublicHomepage lang={lang} stewardOnly={!isAdmin}/> : area==="schedule" ? <EditorialScheduleV1 lang={lang}/> : area==="social" ? <SocialMediaKitV1 lang={lang}/> : <>
      {error&&<p role="alert" style={{color:"#9a2c2c"}}>{error}</p>}
      {message&&<p role="status" style={{color:"#176044"}}>{message}</p>}
      {loading&&<p>Loading editorial content…</p>}
      {draft&&FIELDS.map(section=><section key={section.key} className="ns-card" style={{padding:18,marginBottom:14}}>
        <h2 style={{marginTop:0,fontSize:20}}>{section.title}</h2>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(250px,1fr))",gap:12}}>
          {section.fields.map(([key,label])=><label key={key} style={{display:"grid",gap:6,fontWeight:600}}>
            {label}
            {["fullText","fullTextFr","teaser","teaserFr","quote","quoteFr","works"].includes(key)
              ? <textarea rows={key.startsWith("fullText")?5:3} className="ns-input" style={{width:"100%"}} value={storedValue(draft?.[section.key]?.[key],key)} onChange={e=>change(section.key,key,e.target.value)}/>
              : <input className="ns-input" style={{width:"100%"}} value={storedValue(draft?.[section.key]?.[key],key)} onChange={e=>change(section.key,key,e.target.value)}/>}
          </label>)}
        </div>
      </section>)}
      {draft&&<button className="ns-button ns-button-primary" type="button" disabled={!dirty||saving} onClick={()=>void save()}>
        {saving?"Saving…":"Save Spotlight"}
      </button>}
    </>}
  </div>;
}
